import {availabilitySubmission} from '../../shared/availability';
import {availabilityDisplayModel} from '../Calendar/availabilityDisplay';
import {agendaParticipants,isAgendaEventCancelled} from '../Calendar/dayAgenda';
import {overviewAvailabilityWarnings} from './overviewEventAvailability';
import {teamRoleLabel} from '../../utils/teamRoleDisplay';
import {parseSetupKey,resolveSetupEvent} from '../../utils/eventSetup';

export const MANAGER_SECTIONS={attention:'Manager Attention',availability:'Team Availability',uploads:'Recent uploads'};
export const managerMemberId=member=>typeof member==='string'?member:member?.uid||member?.userId||member?.id;
export function managerMillis(value) {
  const parsed=typeof value?.toMillis==='function'?value.toMillis():typeof value?.toDate==='function'?value.toDate().getTime():
    typeof value?.seconds==='number'?value.seconds*1000:value instanceof Date?value.getTime():
      typeof value==='number'?(value>1e12?value:value*1000):typeof value==='string'?Date.parse(value):0;
  return Number.isFinite(parsed)?parsed:0;
}
export function normalizeManagerLayout(value={}) {
  const list=(input,max)=>Array.isArray(input)?[...new Set(input.filter(item=>typeof item==='string'))].slice(0,max):[];
  return {version:1,teamOrder:list(value.teamOrder,200),hiddenTeamIds:list(value.hiddenTeamIds,200),sections:Array.isArray(value.sections)?list(value.sections,3).filter(id=>MANAGER_SECTIONS[id]):Object.keys(MANAGER_SECTIONS),
    columns:[2,3,4].includes(value.columns)?value.columns:3,density:value.density==='compact'?'compact':'comfortable',
    reminders:(Array.isArray(value.reminders)?value.reminders:[]).filter(item=>item&&typeof item.id==='string'&&typeof item.teamId==='string'&&typeof item.title==='string'&&/^\d{4}-\d{2}-\d{2}$/.test(item.weekKey)).slice(0,200)
      .map(item=>({id:item.id.slice(0,100),teamId:item.teamId.slice(0,128),title:item.title.slice(0,200),weekKey:item.weekKey,done:item.done===true})),
    dismissedComments:list(value.dismissedComments,500)};
}
export function managerRoster(team={},profiles={}) {
  return [...new Map((Array.isArray(team.members)?team.members:[]).filter(Boolean).map(member=>{
    const uid=managerMemberId(member),embedded=typeof member==='object'?member:{},profile=profiles[uid]||{};
    const raw=team.teamRoles?.[uid]||embedded.role||'Spectator';
    return [uid,{uid,name:profile.username||embedded.username||team.roster?.[uid]?.username||team.memberUsernames?.[uid]||'Member',role:teamRoleLabel(typeof raw==='object'?raw.role:raw)}];
  }).filter(([uid])=>uid)).values()];
}
export function managerSubmission(records,members,weekKey,zone) {
  const players=members.filter(member=>member.role==='Player');
  const normalized=records.map(record=>({...record,userId:record.userId||record.uid||record.userUid||record.createdByUid||members.find(member=>member.name.toLowerCase()===String(record.username||record.userName||'').toLowerCase())?.uid}));
  const result=availabilitySubmission(normalized,players.map(member=>member.uid),weekKey,zone);
  return {total:players.length,count:players.filter(member=>result.submitted.has(member.uid)).length,
    missing:players.filter(member=>!result.submitted.has(member.uid)),unavailable:players.filter(member=>result.unavailable.has(member.uid))};
}
export function managerAvailability({team,records,members,week,zone,allEvents=[]}) {
  const ids=new Set(members.map(member=>member.uid));
  const busyByUid={};
  allEvents.filter(event=>event.__teamId!==team.id&&!isAgendaEventCancelled(event)).forEach(event=>{
    const uids=event.participantsUid?.length?event.participantsUid:members.filter(member=>(event.participants||[]).some(name=>String(name).toLowerCase()===member.name.toLowerCase())).map(member=>member.uid);
    uids.filter(uid=>ids.has(uid)&&event.participantStates?.[uid]?.status!=='declined').forEach(uid=>{
      (busyByUid[uid]||=[]).push([Date.parse(event.startUTC),Date.parse(event.endUTC)]);
    });
  });
  return availabilityDisplayModel({date:new Date(week[0].year,week[0].month-1,week[0].day,12),zone,display:'list',records,members,selectedMembers:members,
    roles:Object.fromEntries(members.map(member=>[member.uid,member.role])),teamZone:team.teamTimeZone||team.timeZone||zone,
    events:(team.events||[]).filter(event=>event&&!isAgendaEventCancelled(event)).map(event=>({...event,calendar:'team',__teamId:team.id})),busyByUid});
}
export function managerConflicts(events,team,members,records,zone,now) {
  const roster=new Set(members.map(member=>member.uid));
  return events.filter(event=>event.__teamId===team.id&&!isAgendaEventCancelled(event)&&Date.parse(event.endUTC)>now).flatMap(event=>{
    const people=agendaParticipants(event,members.map(member=>({...member,username:member.name})),team).filter(person=>roster.has(person.uid));
    const warnings=overviewAvailabilityWarnings(event,people,{ready:true,records},zone);
    return people.flatMap(person=>{
      const status=String(person.status||'').toLowerCase();
      const reason=status==='declined'?'Declined':status==='pending'?'Confirmation pending':warnings[person.uid||person.name]?'Potential conflict':null;
      return reason?[{event,person,reason}]:[];
    });
  });
}
export function managerComments(rows,uid,username,team,dismissed=[]) {
  const role=team.teamRoles?.[uid],manager=team.teamLeaderId===uid||teamRoleLabel(role&&typeof role==='object'?role.role:role)==='Manager';
  const list=value=>Array.isArray(value)?value:[];
  return rows.filter(row=>!row.deleted&&!row.resolved&&!row.parentId&&!['resolved','closed'].includes(row.status)&&!dismissed.includes(`${team.id}/${row.id}`)&&
    (row.userId||row.authorUid||row.createdByUid)!==uid).filter(row=>{
    const targets=[row.targetUid,row.recipientUid,row.assigneeUid,...list(row.targetUids),...list(row.recipientUids)].filter(Boolean);
    const tags=[...list(row.at),...list(row.mentions)].map(tag=>String(tag).replace(/^@/,'').toLowerCase());
    return targets.length?targets.includes(uid):tags.length?tags.includes(String(username).toLowerCase())||tags.includes(uid)||(manager&&tags.includes('manager')):manager;
  }).sort((a,b)=>managerMillis(b.createdAt)-managerMillis(a.createdAt));
}
export function managerUploadEvent(item,events) {
  const key=item.calendarEventId||item.setupEventId||item.eventId||item.sourceEventId;
  const identity=parseSetupKey(key);
  const resolved=key?resolveSetupEvent(events.filter(Boolean),key):null;
  return resolved||events.find(event=>(event.mapSlots||[]).some(slot=>item.kind==='stats'?slot.statsId===item.id:
    slot.mapId===item.mapId&&(slot.matchId===item.matchId||(slot.id===identity?.slotId&&event.eventId===identity.eventId))))||null;
}
export function managerTeamStatus(team={}) {
  // Operational state lives on the team, not on subscription entitlements.
  const raw=team.activityStatus||team.teamStatus||team.status;
  const status=typeof raw==='string'?raw.trim():'';
  const labels={active:'Active','on break':'On Break',on_break:'On Break',inactive:'Inactive',disbanded:'Disbanded',draft:'Draft',rebuilding:'Rebuilding',trialing:'Trialing',trialling:'Trialing','billing hold':'Billing Hold','past due':'Past Due',canceled:'Canceled',cancelled:'Canceled'};
  const label=team.disbandV2?.status==='scheduled'?'Disbandment scheduled':team.disbandV2?.status==='finalizing'?'Disbanding':labels[status.toLowerCase()]||status||'Status unavailable';
  return {label,active:label==='Active'};
}
export function orderManagerTeams(teams,order=[]) {
  const positions=new Map(order.map((id,index)=>[id,index]));
  return [...teams].sort((a,b)=>{
    const left=positions.get(a.id),right=positions.get(b.id);
    if(left!==undefined||right!==undefined)return (left??Infinity)-(right??Infinity);
    return Number(managerTeamStatus(b).active)-Number(managerTeamStatus(a).active)||a.name.localeCompare(b.name);
  });
}
export function managerTimeRange(day,window,zone) {
  const at=minutes=>day.startOf('day').plus({minutes}).setZone(zone).toUTC().toISO();
  return {startUTC:at(window.fromMin),endUTC:at(window.toMin)};
}
